import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';

export const LoginPage: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    organizationId: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Валидация формы
  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.username.trim()) {
      errors.username = 'Имя пользователя обязательно';
    } else if (formData.username.length < 3) {
      errors.username = 'Имя пользователя должно содержать минимум 3 символа';
    }

    if (!formData.password) {
      errors.password = 'Пароль обязателен';
    } else if (formData.password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Обработка изменения полей формы
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Очистка ошибки для поля при изменении
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Обработка отправки формы
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(
        formData.username.trim(),
        formData.password,
        formData.organizationId.trim() || undefined
      );
      
      // Перенаправляем на страницу, с которой пришел пользователь, или на дашборд
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Ошибка входа:', error);
    }
  };

  // Переключение видимости пароля
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  // Обработка нажатия Enter
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit(event as any);
    }
  };

  return (
    <>
      <Helmet>
        <title>Вход в систему - ELEKTROM</title>
        <meta name="description" content="Авторизация в системе управления очередями ELEKTROM" />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Card
            elevation={10}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Логотип и заголовок */}
              <Box textAlign="center" mb={4}>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    textAlign: 'center',
                  }}
                >
                  ELEKTROM
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Система управления очередями
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Войдите в свою учетную запись для продолжения
                </Typography>
              </Box>

              {/* Сообщение об ошибке */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Форма входа */}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Имя пользователя"
                  variant="outlined"
                  value={formData.username}
                  onChange={handleChange('username')}
                  onKeyPress={handleKeyPress}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Пароль"
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={formData.password}
                  onChange={handleChange('password')}
                  onKeyPress={handleKeyPress}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="ID организации (опционально)"
                  variant="outlined"
                  value={formData.organizationId}
                  onChange={handleChange('organizationId')}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Оставьте пустым для автоматического определения"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    height: 48,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    },
                  }}
                >
                  {isLoading ? <LoadingSpinner size={24} /> : 'Войти в систему'}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }}>или</Divider>

              {/* Дополнительные ссылки */}
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" paragraph>
                  Нет доступа к системе?{' '}
                  <Link href="#" color="primary" underline="hover">
                    Обратитесь к администратору
                  </Link>
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Забыли пароль?{' '}
                  <Link href="#" color="primary" underline="hover">
                    Восстановить доступ
                  </Link>
                </Typography>
              </Box>

              {/* Информация о системе */}
              <Box mt={4} p={2} bgcolor="grey.50" borderRadius={2}>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
                  ELEKTROM v1.0.0 | Система управления электронными очередями
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5}>
                  Для корректной работы требуется JavaScript
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};
